import { useState } from "react";
import {
  MdAccessTime,
  MdInfoOutline,
  MdLogin,
  MdLogout,
  MdPeopleOutline,
  MdVerifiedUser,
} from "react-icons/md";
import { useCurrentUser } from "../../../../platform_core/auth/services/AuthService";
import "./TrackTime.scss";

interface StampState {
  isClockedIn: boolean;
  changedAt: Date;
}

const stampDateFormatter = new Intl.DateTimeFormat("de-DE", {
  day: "2-digit",
  month: "2-digit",
  year: "numeric",
  hour: "2-digit",
  minute: "2-digit",
  second: "2-digit",
});

export default function TrackTime() {
  const { data: currentUser, isPending, isError } = useCurrentUser();
  const [stamps, setStamps] = useState<Record<string, StampState>>({});
  const employeeId = currentUser?.userId;
  const stamp = employeeId ? stamps[employeeId] : undefined;
  const isClockedIn = stamp?.isClockedIn ?? false;
  const employeeName = currentUser?.displayName || currentUser?.userName;

  const handleStamp = () => {
    if (!employeeId) return;

    const changedAt = new Date();
    setStamps((previous) => ({
      ...previous,
      [employeeId]: {
        isClockedIn: !previous[employeeId]?.isClockedIn,
        changedAt,
      },
    }));
  };

  return (
    <section className="sales-layout__page track-time">
      <header className="track-time__header">
        <h1>Zeiterfassung</h1>
        <p>Arbeitszeit erfassen und den aktuellen Stempelstatus einsehen.</p>
      </header>

      <div className="track-time__cards">
        <section className="track-time__card" aria-labelledby="track-time-employee-title">
          <header className="track-time__card-header">
            <span className="track-time__section-icon" aria-hidden="true">
              <MdPeopleOutline />
            </span>
            <h2 id="track-time-employee-title">Mitarbeiter für die Stempelzeiterfassung</h2>
          </header>
          <div className="track-time__card-body">
            <div className="track-time__field">
              <label htmlFor="track-time-employee">Mitarbeiter</label>
              <select
                key={employeeId ?? "loading"}
                id="track-time-employee"
                defaultValue={employeeId ?? ""}
                disabled={!employeeId || isError}
                aria-describedby="track-time-employee-hint"
              >
                {employeeId ? (
                  <option value={employeeId}>{employeeName}</option>
                ) : (
                  <option value="">
                    {isPending ? "Mitarbeiter wird geladen …" : "Kein Mitarbeiter verfügbar"}
                  </option>
                )}
              </select>
              <p id="track-time-employee-hint" className="track-time__hint">
                Aktuell steht der angemeldete Benutzer zur Verfügung.
              </p>
            </div>
            {isError && (
              <p className="track-time__error" role="alert">
                Der Mitarbeiter konnte nicht geladen werden. Bitte laden Sie die Seite erneut.
              </p>
            )}
          </div>
        </section>

        <section className="track-time__card" aria-labelledby="track-time-status-title">
          <header className="track-time__card-header">
            <span className="track-time__section-icon" aria-hidden="true">
              <MdInfoOutline />
            </span>
            <h2 id="track-time-status-title">Stempelstatus</h2>
          </header>
          <div
            className="track-time__card-body track-time__status"
            role="status"
            aria-live="polite"
            aria-atomic="true"
          >
            <span
              className={`track-time__status-badge${isClockedIn ? " track-time__status-badge--active" : ""}`}
            >
              <MdAccessTime aria-hidden="true" />
              {!employeeId || isError ? "Kein Mitarbeiter ausgewählt" : isClockedIn ? "Eingestempelt" : "Ausgestempelt"}
            </span>
            {stamp ? (
              <span className="track-time__status-since">
                seit <time dateTime={stamp.changedAt.toISOString()}>{stampDateFormatter.format(stamp.changedAt)}</time>
              </span>
            ) : (
              <span className="track-time__status-since">Noch keine Stempelung in dieser Ansicht.</span>
            )}
          </div>
        </section>

        <section className="track-time__card" aria-labelledby="track-time-action-title">
          <header className="track-time__card-header">
            <span className="track-time__section-icon" aria-hidden="true">
              <MdVerifiedUser />
            </span>
            <h2 id="track-time-action-title">Aktion wählen</h2>
          </header>
          <div className="track-time__card-body track-time__action">
            <button
              type="button"
              className="track-time__stamp-button"
              onClick={handleStamp}
              disabled={!employeeId || isError}
            >
              {isClockedIn ? <MdLogout aria-hidden="true" /> : <MdLogin aria-hidden="true" />}
              {isClockedIn ? "Ausstempeln" : "Einstempeln"}
            </button>
          </div>
        </section>
      </div>

      <p className="track-time__preview-note">
        <MdInfoOutline aria-hidden="true" />
        Vorschau: Stempelungen werden noch nicht dauerhaft gespeichert und beim Verlassen der Ansicht zurückgesetzt.
      </p>
    </section>
  );
}
